// Блоки рекомендаций в отчёте карьерного теста (часть C, 13.10.2026).
//
// Строки — в таблице report_offers (миграция 20261023120000). Здесь — всё,
// что от базы не зависит и поэтому проверяется без неё:
//   · выбор одной записи на слот (активна, в датах, группа и архетип
//     подходят, больше priority);
//   · лимиты знаков на поле по самому длинному языку — вместе с высотой
//     слота (subjects ≤ 140 px, map ≤ 160 px, final ≤ 180 px) они держат
//     блок внутри страницы;
//   · персональная ссылка для ссылки и QR: короткий адрес yolutap.com/<short>,
//     архетипы, предмет, utm_source=report, utm_medium=web|pdf|print,
//     utm_campaign=<слот>;
//   · куда ведёт короткий адрес (301 с сохранением параметров);
//   · ключевой предмет отчёта → код предмета карты репетиторов.
//
// Тексты могут содержать {countries} — число стран подбора, тот же источник и
// склонения, что у превью (#416, src/edu/countries-count.ts).

import { ABROAD_GUEST_START_PATH, GUEST_ARCHETYPE_CODES } from "@/edu/guest";
import { withCountries } from "@/edu/countries-count";
import { isSubjectOf } from "@/data/map-taxonomy";

export const OFFER_SLOTS = ["subjects", "map", "final"] as const;
export type OfferSlot = (typeof OFFER_SLOTS)[number];
export type OfferLang = "az" | "ru" | "en";
export type OfferMedium = "web" | "pdf" | "print";
export const OFFER_AGE_GROUPS = ["10_11", "12_13", "14_15", "16_18", "18_22"] as const;

/** Строка report_offers — так, как её отдаёт база. */
export type ReportOfferRow = {
  id: string;
  slot: OfferSlot;
  kind: "own" | "partner";
  active: boolean;
  priority: number;
  age_groups: string[] | null;
  archetypes: string[] | null;
  starts_at: string | null;
  ends_at: string | null;
  url_template: string;
  short_path: string | null;
  is_test?: boolean;
  created_at?: string;
} & Record<`${"title" | "text" | "cta" | "qr_note"}_${OfferLang}`, string | null>;

/** Блок, готовый к отрисовке: тексты на языке отчёта, {countries} подставлено. */
export type ReportOfferView = {
  id: string;
  slot: OfferSlot;
  kind: "own" | "partner";
  title: string;
  text: string;
  cta: string;
  qrNote: string;
  urlTemplate: string;
  shortPath: string | null;
  /** Число стран, подставленное в текст (для автотеста: совпадает с #416). */
  countries: number | null;
};

/** Блоки на всех трёх языках: отчёт можно открыть на другом языке (?lang). */
export type ReportOffersBundle = {
  byLang: Record<OfferLang, Partial<Record<OfferSlot, ReportOfferView>>>;
  countries: number | null;
};

export const EMPTY_OFFERS: ReportOffersBundle = { byLang: { az: {}, ru: {}, en: {} }, countries: null };

/** Высота слота, px (C2). Блок верстается не выше и обрезается overflow:hidden. */
export const SLOT_MAX_PX: Record<OfferSlot, number> = { subjects: 140, map: 160, final: 180 };

/**
 * Лимиты знаков по полям — по самому длинному языку, с подставленным числом
 * стран. Подобраны по вёрстке блока: при этих длинах блок помещается в свою
 * высоту на ширине страницы (замер автотестом verify-report-pages).
 */
export const FIELD_LIMITS: Record<OfferSlot, { title: number; text: number; cta: number; qrNote: number }> = {
  final: { title: 44, text: 130, cta: 34, qrNote: 40 },
  subjects: { title: 44, text: 100, cta: 22, qrNote: 24 },
  map: { title: 44, text: 120, cta: 22, qrNote: 24 },
};

const fieldOf = (row: ReportOfferRow, f: "title" | "text" | "cta" | "qr_note", lang: OfferLang): string =>
  (row[`${f}_${lang}`] ?? "").trim();

/** Лимиты строки на всех трёх языках. Пустой список — всё в норме. */
export function offerLimitProblems(row: ReportOfferRow, countries: number | null): string[] {
  const lim = FIELD_LIMITS[row.slot];
  const out: string[] = [];
  for (const lang of ["az", "ru", "en"] as const) {
    const t = {
      title: fieldOf(row, "title", lang),
      text: withCountries(fieldOf(row, "text", lang), lang, countries ?? 99),
      cta: fieldOf(row, "cta", lang),
      qrNote: fieldOf(row, "qr_note", lang),
    };
    for (const k of ["title", "text", "cta", "qrNote"] as const) {
      if ([...t[k]].length > lim[k]) out.push(`${row.slot}.${k}_${lang}: ${[...t[k]].length} > ${lim[k]}`);
    }
    if (!t.title) out.push(`${row.slot}.title_${lang}: пусто`);
  }
  return out;
}

/**
 * Одна запись на слот: активна, сейчас в пределах дат, группа отчёта и
 * ведущий архетип подходят (пустой список — все), строка проходит лимиты.
 * Из подходящих — больше priority, затем раньше created_at. Партнёр
 * возможен только в map: наши блоки он не вытесняет (C5).
 */
export function pickOffers(
  rows: readonly ReportOfferRow[],
  ctx: { ageGroup: string; lead: string | null; now: Date; countries: number | null },
): Partial<Record<OfferSlot, ReportOfferRow>> {
  const now = ctx.now.getTime();
  const fits = (r: ReportOfferRow) =>
    r.active &&
    (r.kind === "partner") === (r.slot === "map") &&
    (!r.starts_at || Date.parse(r.starts_at) <= now) &&
    (!r.ends_at || Date.parse(r.ends_at) > now) &&
    (!r.age_groups?.length || r.age_groups.includes(ctx.ageGroup)) &&
    (!r.archetypes?.length || (!!ctx.lead && r.archetypes.includes(ctx.lead))) &&
    offerLimitProblems(r, ctx.countries).length === 0;
  const out: Partial<Record<OfferSlot, ReportOfferRow>> = {};
  for (const slot of OFFER_SLOTS) {
    const best = rows
      .filter((r) => r.slot === slot && fits(r))
      .sort((a, b) => b.priority - a.priority || String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")))[0];
    if (best) out[slot] = best;
  }
  return out;
}

export function offerView(row: ReportOfferRow, lang: OfferLang, countries: number | null): ReportOfferView {
  return {
    id: row.id,
    slot: row.slot,
    kind: row.kind,
    title: fieldOf(row, "title", lang),
    text: withCountries(fieldOf(row, "text", lang), lang, countries),
    cta: fieldOf(row, "cta", lang),
    qrNote: fieldOf(row, "qr_note", lang),
    urlTemplate: row.url_template,
    shortPath: row.short_path,
    countries: /{countries}/.test(fieldOf(row, "text", lang)) ? countries : null,
  };
}

export function offersBundle(
  rows: readonly ReportOfferRow[],
  ctx: { ageGroup: string; lead: string | null; now: Date; countries: number | null },
): ReportOffersBundle {
  const picked = pickOffers(rows, ctx);
  const byLang: ReportOffersBundle["byLang"] = { az: {}, ru: {}, en: {} };
  for (const [slot, row] of Object.entries(picked) as Array<[OfferSlot, ReportOfferRow]>) {
    for (const lang of ["az", "ru", "en"] as const) byLang[lang][slot] = offerView(row, lang, ctx.countries);
  }
  return { byLang, countries: ctx.countries };
}

// ── Ссылки ──────────────────────────────────────────────────────────────

export const SITE = "https://yolutap.com";

/** Короткие адреса и куда они ведут — маркеры url_template. */
export const SHORT_TARGETS: Record<string, string> = { abroad: "{abroad_start}", "tutors-near": "{tutors}" };

const TUTORS_PATH: Record<OfferLang, string> = { ru: "/ru/tutors", az: "/az/tutors", en: "/en/tutors" };

const isArchetype = (v: string | null | undefined): v is string =>
  !!v && (GUEST_ARCHETYPE_CODES as readonly string[]).includes(v);

export type OfferLinkCtx = {
  lang: OfferLang;
  /** Ведущий и поддерживающий архетип (A I S K L E P B). У 18+ — нет. */
  lead?: string | null;
  supp?: string | null;
  /** Код предмета карты (map-taxonomy), если ключевой предмет отчёта нашёлся. */
  subject?: string | null;
  medium: OfferMedium;
};

/**
 * Персональная ссылка блока — она же содержимое QR. Свои страницы — через
 * короткий адрес (он считает переход и отвечает 301), партнёр без короткого
 * адреса — прямо на его страницу с теми же utm.
 */
export function offerHref(offer: Pick<ReportOfferView, "slot" | "urlTemplate" | "shortPath">, ctx: OfferLinkCtx): string {
  const q = new URLSearchParams();
  if (offer.shortPath) q.set("lang", ctx.lang);
  if (isArchetype(ctx.lead)) q.set("a1", ctx.lead);
  if (isArchetype(ctx.supp) && ctx.supp !== ctx.lead) q.set("a2", ctx.supp);
  if (ctx.subject && offer.urlTemplate === "{tutors}") q.set("subject", ctx.subject);
  q.set("utm_source", "report");
  q.set("utm_medium", ctx.medium);
  q.set("utm_campaign", offer.slot);
  if (offer.shortPath) return `${SITE}/${offer.shortPath}?${q.toString()}`;
  const base = /^https?:\/\//.test(offer.urlTemplate) ? offer.urlTemplate : SITE + "/";
  return base + (base.includes("?") ? "&" : "?") + q.toString();
}

/**
 * Куда ведёт короткий адрес: путь на языке из ?lang= и все параметры, кроме
 * служебного lang. Номера записи в ссылке нет: QR короче, а слот и так в
 * utm_campaign. null — адрес неизвестен (маршрут отвечает 404).
 */
export function shortTarget(shortPath: string, search: URLSearchParams): string | null {
  const marker = SHORT_TARGETS[shortPath];
  if (!marker) return null;
  const raw = search.get("lang");
  const lang: OfferLang = raw === "az" || raw === "en" ? raw : "ru";
  const path = marker === "{abroad_start}" ? ABROAD_GUEST_START_PATH[lang] : TUTORS_PATH[lang];
  const q = new URLSearchParams(search);
  q.delete("lang");
  if (marker !== "{tutors}") q.delete("subject");
  if (marker === "{tutors}") {
    q.delete("a1");
    q.delete("a2");
  }
  const s = q.toString();
  return path + (s ? `?${s}` : "");
}

// ── Ключевой предмет отчёта → предмет карты репетиторов ───────────────

/**
 * Предметы в отчёте — свободный текст на языке отчёта («Математика и
 * алгебра», «Riyaziyyat», «Computer science»). Ищем первое известное слово;
 * код берём из справочника карты (design/content/map-taxonomy.json). Не нашли —
 * ссылка на карту без фильтра (C4).
 */
const SUBJECT_RULES: Array<[RegExp, string]> = [
  [/геометр|həndəs|geometr/i, "geometry"],
  [/математ|алгебр|riyaziyyat|cəbr|\bmath|algebra|statisti/i, "math"],
  [/физик|fizika|physic/i, "physics"],
  [/хими|kimya|chemi/i, "chemistry"],
  [/биолог|biolog/i, "biology"],
  [/информатик|программир|computer science|informatika|proqramla|coding|programming|\bcs\b/i, "informatics"],
  [/истори|tarix|histor/i, "history"],
  [/географ|coğrafiya|geograph/i, "geography"],
  [/литератур|ədəbiyyat|literatur/i, "literature"],
  [/английск|ingilis|english/i, "en"],
  [/русск|rus dili|russian/i, "ru_lang"],
  [/азербайджанск|azərbaycan dili|azerbaijani/i, "az_lang"],
  [/черчен|rəsmxət|technical drawing/i, "drawing_tech"],
  [/рисова|изобраз|rəsm|drawing|fine art/i, "drawing"],
  [/логик|məntiq|logic/i, "logic"],
];

export function mapSubjectCode(subjects: readonly (string | null | undefined)[]): string | null {
  for (const s of subjects) {
    if (!s) continue;
    for (const [re, code] of SUBJECT_RULES) if (re.test(s) && isSubjectOf("tutor", code)) return code;
  }
  return null;
}
